// This optional code is used to register a service worker.
// register() is not called by default.

import React from 'react'
import type { JSX } from 'react'
import { toast } from 'react-toastify'
import UpdateToast from './components/UpdateToast'

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === '[::1]' ||
    // 127.0.0.0/8 are considered localhost for IPv4.
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/,
    ),
)

type Config = {
  onSuccess?: (registration: ServiceWorkerRegistration) => void
  onUpdate?: (registration: ServiceWorkerRegistration) => void
}

export function register(config?: Config) {
  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    // The URL constructor is available in all browsers that support SW.
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href)
    if (publicUrl.origin !== window.location.origin) {
      // Our service worker won't work if PUBLIC_URL is on a different origin
      // from what our page is served on. This might happen if a CDN is used to
      // serve assets; see https://github.com/facebook/create-react-app/issues/2374
      return
    }

    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`

      if (isLocalhost) {
        // This is running on localhost. Let's check if a service worker still exists or not.
        checkValidServiceWorker(swUrl, config)

        // Add some additional logging to localhost, pointing developers to the
        // service worker/PWA documentation.
        navigator.serviceWorker.ready.then((registration) => {
          console.log(
            'This web app is being served cache-first by a service ' +
              'worker. To learn more, visit https://cra.link/PWA',
          )

          // Register for background sync
          if ('sync' in registration) {
            console.log('Background sync is supported')
            registration.sync
              .register('tagQueue')
              .then(() => {
                console.log('Background sync registered successfully')
              })
              .catch((err) => {
                console.error('Background sync registration failed:', err)
              })
          } else {
            console.log('Background sync is not supported')
          }
        })
      } else {
        // Is not localhost. Just register service worker
        registerValidSW(swUrl, config)
      }
    })

    // Add listener for online/offline events
    window.addEventListener('online', () => {
      console.log('App is online')
      navigator.serviceWorker.ready.then((registration) => {
        if ('sync' in registration) {
          registration.sync
            .register('tagQueue')
            .then(() => console.log('Sync registered after coming online'))
            .catch((err) => console.error('Sync registration failed:', err))
        }
      })
    })

    window.addEventListener('offline', () => {
      console.log('App is offline')
    })
  }
}

function registerValidSW(swUrl: string, config?: Config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      registration.onupdatefound = () => {
        const installingWorker = registration.installing
        if (installingWorker == null) {
          return
        }
        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // At this point, the updated precached content has been fetched,
              // but the previous service worker will still serve the older
              // content until all client tabs are closed.
              console.log(
                'New content is available and will be used when all ' +
                  'tabs for this page are closed. See https://cra.link/PWA.',
              )

              // Show update notification toast
              toast.info(React.createElement(UpdateToast), {
                autoClose: false,
                closeOnClick: false,
                draggable: false, // Prevent drag to dismiss
                closeButton: false, // Remove the close button
                style: {
                  padding: 0,
                  backgroundColor: 'transparent',
                },
                icon: false,
              })

              // Execute callback
              config?.onUpdate?.(registration)
            } else {
              // At this point, everything has been precached.
              // It's the perfect time to display a
              // "Content is cached for offline use." message.
              console.log('Content is cached for offline use.')

              // Execute callback
              config?.onSuccess?.(registration)

              // Wait for service worker to be fully active before registering sync
              setTimeout(() => {
                if ('sync' in registration) {
                  registration.sync
                    .register('tagQueue')
                    .then(() =>
                      console.log(
                        'Background sync registered after activation',
                      ),
                    )
                    .catch((err) =>
                      console.error(
                        'Background sync registration failed:',
                        err,
                      ),
                    )
                }
              }, 1000)
            }
          }
        }
      }
    })
    .catch((error) => {
      console.error('Error during service worker registration:', error)
    })
}

function checkValidServiceWorker(swUrl: string, config?: Config) {
  // Check if the service worker can be found. If it can't reload the page.
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then((response) => {
      // Ensure service worker exists, and that we really are getting a JS file.
      const contentType = response.headers.get('content-type')
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        // No service worker found. Probably a different app. Reload the page.
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload()
          })
        })
      } else {
        // Service worker found. Proceed as normal.
        registerValidSW(swUrl, config)
      }
    })
    .catch(() => {
      console.log(
        'No internet connection found. App is running in offline mode.',
      )
    })
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister()
      })
      .catch((error) => {
        console.error(error.message)
      })
  }
}
